import { playheadMsAt, SCHEDULE_LEAD_MS, Soundfont, useAudioEngine, useInstrument } from '@tunasong/audio-ui'
import { MelodyLib } from '@tunasong/music-lib'
import type { Melody, MelodyChordLane, Scale } from '@tunasong/schemas'
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { melodyInstrument } from './instrument-hint.js'

/**
 * Play a melody with its feel, and run a play head across the roll.
 *
 * The timing comes from `MelodyLib.scheduleMelody` - pure and tested to the millisecond without
 * audio - and goes straight to `engine.playScheduled`, which puts every note on the Tone transport
 * at its own absolute time. Nothing on this path rounds a millisecond. `playSequence` could not be
 * used: it walks note-to-note by duration and has no absolute time, so a 12 ms offset would be
 * silently discarded at the one place the offset means anything.
 *
 * The play head runs on the AUDIO clock, not on wall-clock time. It used to start at
 * `performance.now()` when `play()` was called, which put it several hundred ms ahead of the sound:
 * the audio is displaced from that moment by the engine's schedule lead (120 ms), the Tone context's
 * lookAhead (100 ms) and the device's output latency. Rather than subtract a constant for those -
 * two are configuration and the third is hardware - the loop reads `engine.outputTime()` against the
 * start `engine.playScheduled` reports, so every present and future offset is accounted for by
 * construction. @see `playback-clock.ts` in @tunasong/audio-ui
 *
 * Melody toolkit M2 adds the chords: `MelodyLib.scheduleChords` voices the lane under the melody, on a
 * piano of its own, and a drone stands in when there are no chords. Every note goes on under a schedule
 * group, so Stop takes back what was scheduled instead of letting it ring on. Loop places each next pass
 * half a second before the current one ends, against the origin a schedule made then would get, so it
 * lands on the bar line.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §4.2;
 * docs/ux/2026-10-23-melody-writing-toolkit-ux.md §5.1
 */

/** The chords' timbre: Bowie's voicings are piano voicings */
const CHORD_INSTRUMENT = 'acoustic_grand_piano'

/** How far ahead of a pass's end the next one is scheduled: past the schedule lead and the lookahead */
const LOOP_AHEAD_MS = 500

export interface UseMelodyRollPlaybackProps {
  melody: Melody
  /** Play with `offsetMs` treated as zero, so the feel can be A/B'd. */
  quantized?: boolean
  /** The resolved chord lane (melody toolkit M2). Played only with `isChordsOn` */
  lane?: MelodyChordLane
  /** Play the chords, or the drone when there are none. Off by default: the melody alone, as before */
  isChordsOn?: boolean
  /** Play the melody. On by default; off for singing over the chords */
  isMelodyOn?: boolean
  /** Repeat the whole melody, in whole bars, until stopped */
  isLooping?: boolean
  /** With no chords in the lane, hold this key's root and fifth (spec §7, §8) */
  drone?: Scale
  /** Beats of count-in before beat 1, the first chord sounding through them (Sing an idea, spec §5.2) */
  leadInBeats?: number
}

export interface MelodyRollPlayback {
  play: () => void
  /**
   * Play, resolving when the melody has finished sounding on the audio clock, or when stopped (Pitch
   * Trainer Phase 2, plan M6a step 6.2): the capture window opens the microphone straight after it.
   */
  playToEnd: () => Promise<void>
  /**
   * Play from the moment `engine.play` sounds, the engine's schedule lead taken off: a capture's click
   * goes through `engine.play`, so backing that keeps time with it starts here (melody toolkit M2)
   */
  playWithClick: () => void
  /** Play another melody through this playback, as an audition: a take, over the same chords */
  playMelody: (melody: Melody) => void
  stop: () => void
  isPlaying: boolean
  /** ms from the start of the melody, or null when stopped. */
  playheadMs: number | null
}

export const useMelodyRollPlayback = (props: UseMelodyRollPlaybackProps): MelodyRollPlayback => {
  const {
    melody,
    quantized = false,
    lane,
    isChordsOn = false,
    isMelodyOn = true,
    isLooping = false,
    drone,
    leadInBeats = 0,
  } = props
  const engine = useAudioEngine()
  const instrumentName = melodyInstrument(melody.instrumentHint)

  /**
   * One Soundfont per instrument name, not per render: `useInstrument` registers what it is given,
   * and a fresh object each render would churn the engine's instrument table. Same shape as
   * `usePlayNotes`.
   */
  const instrument = useMemo(
    () => new Soundfont(engine.mixer.context, instrumentName),
    [engine.mixer.context, instrumentName]
  )

  /**
   * Route to THIS hook's instrument. `engine.playScheduled(events)` with no id resolves to the
   * first instrument registered on the page, so a melody on a page that also has a piano keyboard
   * would sound on whichever mounted first - and the failure is silent. @see useInstrument
   */
  const instrumentId = useInstrument('melody', instrument) ?? undefined
  const chordInstrument = useMemo(() => new Soundfont(engine.mixer.context, CHORD_INSTRUMENT), [engine.mixer.context])
  const chordInstrumentId = useInstrument('melody-chords', chordInstrument) ?? undefined

  /** This playback's schedule groups: Stop takes back these and nothing else the engine is playing */
  const playbackId = useId()
  const melodyGroup = `melody-${playbackId}`
  const chordGroup = `melody-chords-${playbackId}`

  const [playheadMs, setPlayheadMs] = useState<number | null>(null)
  const frame = useRef<number | null>(null)
  /** Resolves the running `playToEnd`, if any */
  const finished = useRef<(() => void) | null>(null)

  const stop = () => {
    engine.cancelScheduled(melodyGroup)
    engine.cancelScheduled(chordGroup)
    if (frame.current !== null) {
      cancelAnimationFrame(frame.current)
      frame.current = null
    }
    setPlayheadMs(null)
    finished.current?.()
    finished.current = null
  }

  /** Unmounting takes back what this playback scheduled, and ends its play head and its wait */
  useEffect(
    () => () => {
      engine.cancelScheduled(melodyGroup)
      engine.cancelScheduled(chordGroup)
      if (frame.current !== null) {
        cancelAnimationFrame(frame.current)
      }
      finished.current?.()
    },
    [engine, melodyGroup, chordGroup]
  )

  /** The bars the lane and the notes cover, at least one: a loop's length */
  const passBeats = (source: Melody) => {
    const bar = MelodyLib.barBeats(source.tempo.meter)
    const noteEnd = source.notes.reduce((last, note) => Math.max(last, note.onset + note.duration), 0)
    const end = Math.max(noteEnd, lane ? MelodyLib.laneEnd(lane) : 0)
    return Math.max(bar, Math.ceil(end / bar - MelodyLib.GRID_TOLERANCE) * bar)
  }

  /** One pass's events: the melody's, and the chords' or the drone's */
  const passEvents = (source: Melody) => {
    const leadMs = leadInBeats * MelodyLib.beatMs(source.tempo.bpm)
    const melodyEvents = isMelodyOn
      ? MelodyLib.scheduleMelody(source, { quantized }).map(event => ({ ...event, time: event.time + leadMs }))
      : []
    const hasChords = lane !== undefined && lane.chords.length > 0
    const chordEvents = !isChordsOn
      ? []
      : hasChords
        ? MelodyLib.scheduleChords(source, lane, { isMelodyOn, leadInBeats })
        : drone
          ? MelodyLib.scheduleDrone(source, drone, passBeats(source))
          : []
    return { melodyEvents, chordEvents }
  }

  /**
   * Schedule one pass, `offsetMs` after the origin a schedule made now gets. Returns that origin, or null
   * when there was nothing to schedule.
   */
  const schedulePass = (offsetMs: number, source: Melody) => {
    const { melodyEvents, chordEvents } = passEvents(source)
    /*
     * `ScheduledMelodyEvent.time` is ms from the start of the melody; `ScheduledNote.timeMs` is ms from the
     * schedule call. They differ by the pass's offset, so the mapping is explicit.
     */
    const toNotes = (events: MelodyLib.ScheduledMelodyEvent[]) =>
      events.map(event => ({
        midi: event.midi,
        velocity: event.velocity,
        timeMs: event.time + offsetMs,
        durationMs: event.durationMs,
      }))
    const melodyOrigin =
      melodyEvents.length > 0 ? engine.playScheduled(toNotes(melodyEvents), instrumentId, melodyGroup) : null
    const chordOrigin =
      chordEvents.length > 0 ? engine.playScheduled(toNotes(chordEvents), chordInstrumentId, chordGroup) : null
    const totalMs = [...melodyEvents, ...chordEvents].reduce(
      (latest, event) => Math.max(latest, event.time + event.durationMs),
      0
    )
    return { origin: melodyOrigin ?? chordOrigin, totalMs }
  }

  /** Start playing `source` `offsetMs` after the origin a schedule made now gets */
  const start = (offsetMs: number, source: Melody) => {
    stop()
    const first = schedulePass(offsetMs, source)
    if (first.origin === null) {
      return
    }
    /** `start` is the AudioContext time the current pass's origin sounds at: the play head's zero */
    let passStart = first.origin + offsetMs / 1000
    const loopMs = passBeats(source) * MelodyLib.beatMs(source.tempo.bpm)
    const totalMs = isLooping ? loopMs : first.totalMs
    let isNextScheduled = false

    const tick = () => {
      const elapsed = playheadMsAt(engine.outputTime(), passStart)
      if (isLooping) {
        if (!isNextScheduled && elapsed >= totalMs - LOOP_AHEAD_MS) {
          const nextStart = passStart + totalMs / 1000
          schedulePass((nextStart - engine.scheduleOrigin()) * 1000, source)
          isNextScheduled = true
        }
        if (elapsed >= totalMs) {
          passStart += totalMs / 1000
          isNextScheduled = false
          setPlayheadMs(playheadMsAt(engine.outputTime(), passStart))
          frame.current = requestAnimationFrame(tick)
          return
        }
      } else if (elapsed >= totalMs) {
        stop()
        return
      }
      setPlayheadMs(elapsed)
      frame.current = requestAnimationFrame(tick)
    }
    /** Zero until the first note is audible - `playheadMsAt` clamps the lead away. */
    setPlayheadMs(playheadMsAt(engine.outputTime(), passStart))
    frame.current = requestAnimationFrame(tick)
  }

  const play = () => start(0, melody)
  const playWithClick = () => start(-SCHEDULE_LEAD_MS, melody)
  /** Melody toolkit M5: audition a take, its notes over the same chords, toggles and loop */
  const playMelody = (source: Melody) => start(0, source)

  const playToEnd = () =>
    new Promise<void>(resolve => {
      play()
      if (frame.current === null) {
        /** Nothing was scheduled: nothing to wait for */
        resolve()
        return
      }
      finished.current = resolve
    })

  return { play, playToEnd, playWithClick, playMelody, stop, isPlaying: playheadMs !== null, playheadMs }
}
